'use client';

import { useEffect, useState } from 'react';
import HeroSection from '@/sections/HeroSection';
import ContactSection from '@/sections/ContactSection';
import FAQSection from '@/sections/FAQSection';
import LocationSection from '@/sections/LocationSection';
import NavbarContainer from '@/components/client/NavbarContainer';
import FooterWrapper from '@/components/ui/FooterWrapper';
import Popup from '@/components/ui/Popup/Popup';
import { useLoading } from '@/contexts/LoadingContext';
import { useAnimatedNavigate } from '@/hooks/useAnimatedNavigation';
import useAspectRatio from '@/hooks/useAspectRatio';
import {
  BranchSectionContent,
  BrandInfoSectionContent,
  CTASectionContent,
  FAQSectionContent,
  LanguageType,
  NavbarSectionContent,
  PageData,
} from '@/lib/types';
import { debug } from '@/lib/debug';
import { setLanguage } from '@/lib/language';
interface ContactPageClientProps {
  pageData: PageData | null;
  initialLanguage: LanguageType;
}

// TODO: same issue as in services page. with the footer not showing up at the right time.

export default function ContactPageClient({ pageData, initialLanguage }: ContactPageClientProps) {
  const language: LanguageType = initialLanguage;
  const aspectRatio = useAspectRatio();
  const { setAspectRatioReady } = useLoading();
  const animatedNavigate = useAnimatedNavigate();

  // debug.log("contact page rerender");

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('navigationTransitionReady'));
  }, []);

  // Notify loading system when aspect ratio is ready
  useEffect(() => {
    if (aspectRatio !== 0) {
      setAspectRatioReady(true);
    }
  }, [aspectRatio, setAspectRatioReady]);

  const [showPopup, setShowPopup] = useState(true);

  if (!pageData) {
    debug.log('--------------------------------');
    debug.log('PAGE DATA IS NULL');
    debug.log('--------------------------------');
    return null; // TODO: Or a loading spinner
  }

  const ctaSectionPageData: CTASectionContent = pageData.sections.find(section => section.type === 'call-to-action')?.content as CTASectionContent;
  const faqSectionPageData: FAQSectionContent = {
    questions: pageData.sections.find(section => section.type === 'faq')?.content
  } as FAQSectionContent;
  const navbarSectionPageData: NavbarSectionContent = pageData.sections.find(section => section.type === 'navbar')?.content as NavbarSectionContent;
  const brandInfoSectionPageData: BrandInfoSectionContent = pageData.sections.find(section => section.type === 'brand-info')?.content as BrandInfoSectionContent;
  const branchInfoSectionPageDataRaw: BranchSectionContent = {
    branches: pageData.sections.find(section => section.type === 'branch')?.content || []
  } as BranchSectionContent;

  // Modify branch data with bilingual content
  const branchInfoSectionPageData: BranchSectionContent = {
    branches: (branchInfoSectionPageDataRaw.branches || []).map(branch => ({
      ...branch,
      openingHoursSatToWed: language === 'arabic' ? '8 ص – 10 م' : '8 AM – 10 PM',
      openingHoursThu: language === 'arabic' ? '8 ص – 8 م' : '8 AM – 8 PM',
      openingHoursFri: language === 'arabic' ? '10 ص – 6:30 م' : '10 AM – 6:30 PM',
      branchAddress: language === 'arabic'
        ? 'لومير سنتر، حي حطين\nRRHA8736، الرياض، السعودية'
        : 'Lumière Center, Hittin\nRRHA8736, Riyadh, SA',
      timetableHeading: language === 'arabic' ? "ساعات العمل" : "Opening hours",
      locationHeading: language === 'arabic' ? "تجدوننا هنا" : "Find us here",
      buttonText: language === 'arabic' ? "انضم إلى ريڤيڤ" : "Join our waitlist",
      satWedLabel: language === 'arabic' ? "السبت - الأربعاء" : "Sat – Wed",
      thursdayLabel: language === 'arabic' ? "الخميس" : "Thu",
      fridayLabel: language === 'arabic' ? "الجمعة" : "Fri",
      googleMapsButtonText: language === 'arabic' ? "توجّه الآن" : "Get directions"
    }))
  };

  return (
    <div className="bg-bg-light-primary flex flex-col section-gaps relative">

      {/* {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-[12px]" style={{ backgroundColor: 'rgba(71, 70, 66, 0.12)' }}>
          <Popup
            image="/Popup-Image.webp"
            imageAlt="Popup image"
            heading="Welcome"
            text="This is a temporary popup for testing."
            button="Got it"
            hint="This will be removed"
            onClose={() => setShowPopup(false)}
          />
        </div>
      )} */}

      <HeroSection
        title={language === 'arabic' ? "تواصلوا معنا" : "Contact us"}
        paragraph={language === 'arabic' ? "رحلتكم نحو الجمال تبدأ بخطوة واحدة. اتصلوا بنا لتتعرفوا على علاجاتنا الفريدة، وانضموا لقائمة الانتظار، وتحصلوا على فرصة الدخول الحصري إلى عالم ريڤيڤ." :"We value each connection as the start of a transformative journey. Contact us to learn about our treatments, join our waitlist, or find out how to gain priority access to our sanctuary."}
        imageSrc="https://dependable-apparel-f6b4f02674.media.strapiapp.com/Contact_Hero_bc75fbd508.webp"
        imageAlt="Mountain landscape"
        language={language}
        titleFont="big"
      />


      <NavbarContainer
        language={language}
        setLanguage={setLanguage}
        promoBarText={navbarSectionPageData?.promoBarText}
        promoBarVisibility={navbarSectionPageData?.promoBarVisibility}
        onJoinRiviveClick={() => animatedNavigate('/signup')}
        brandInfo={brandInfoSectionPageData}
      />

        <ContactSection
          heading={language === 'arabic' ? "نحن هنا لإرشادك في رحلتك الجمالية" : "We're ready to guide your beauty journey"}
          subheading={language === 'arabic' ? "الدعم الشخصي" : "PERSONALIZED SUPPORT"}
          paragraph={language === 'arabic' ? "فريقنا جاهز لتقديم الإرشاد الخبير حول العلاجات، الإجابة على استفساراتك، ومساعدتك في حجز تجربتك التحويلية الحصرية." : "Our team is here to provide expert guidance on treatments, answer your questions, and help you secure your exclusive transformative experience."}
          language={language}
          heading2={language === 'arabic' ? "الشراكات المهنية والمعلومات الرسمية" : "Professional Partnerships\n& Official Information"}
          paragraph2={language === 'arabic' ? "سواء كنتم ضيوفًا، متعاونين أو تمثلون علامة تجارية شريكة تبحث عن التعاون، هذا هو رابطكم تواصلوا مع فريق أعمالنا." :"Whether you're a guest, a collaborator, or a partner brand seeking alignment, this is your direct connection to our business team."}
          subheading3={language === 'arabic' ? "الشراكات الاستراتيجية" : "Strategic Partnerships"}
          paragraph3={language === 'arabic' ? "تواصلوا مع فريق تطوير الأعمال لدينا لاستكشاف الشراكات، التعاونات الاستراتيجية، أو التحالفات الحصرية التي تتماشى مع رؤية ريڤيڤ في إبراز الجمال الطبيعي من خلال الابتكار." : "Connect with our business development team to explore partnerships, strategic collaborations, or exclusive alliances that align with Rivive's vision of revealing natural beauty through innovation."}
          buttonText3={language === 'arabic' ? "business@rivive.sa" : "business@rivive.sa"}
          subheading4={language === 'arabic' ? "بيانات الشركة" : "Company Details"}
          paragraph4={language === 'arabic' ? "شركة ريڤيڤ تعمل بموجب السجل التجاري رقم 1009120433، ويقع مقرها الرئيسي في الرياض، المملكة العربية السعودية.\n\nللمراسلات القانونية، التراخيص، أو السجلات الرسمية، يرجى التواصل معنا عبر البريد الإلكتروني التالي:" : "Rivive is operated under Commercial Registration (CR) number 1009120433, headquartered in Riyadh, Saudi Arabia. For legal documentation, licensing, or official records, please email us at:"}
          buttonText4={language === 'arabic' ? "business@rivive.sa" : "business@rivive.sa"}
          receptionLabel={language === 'arabic' ? "مكتب الاستقبال" : "Our front desk"}
          receptionValue={language === 'arabic' ? "٠١١٥١٠٦٦٩٩" : "011 510 6699"}
          supportLabel={language === 'arabic' ? "تواصلوا معنا عبر الايميل" : "Reach us by mail"}
          supportValue="hello@rivive.sa"
          localTimeLabel={language === 'arabic' ? "الوقت المحلي" : "Local time"}
          localTimeValue="XX:XX (GMT+3)"
          buttonText={language === 'arabic' ? "ارسل رسالتك" : "Send us your message"}
          buttonTextSuccess={language === 'arabic' ? "تم استلام رسالتك" : "Your message is with us!"}
          buttonTextError={language === 'arabic' ? "تعذر ارسال رسالتك" : "Unable to send message"}
          disclaimerText={language === 'arabic' ? "عند إرسال هذا النموذج، فإنك توافق على سياسة الخصوصية الخاصة بنا. نحن نتعامل مع معلوماتك الشخصية بنفس العناية التي نوليها لكل علاج نقدمه." : "By submitting this form, you agree to our Privacy Policy. We treat your personal information with the same care we bring to every treatment."}
          disclaimerTextSuccess={language === 'arabic' ? "نحن ممتنون لاهتمامك! سيتواصل معك أحد المتخصصين قريبًا للإجابة على أسئلتك وإرشادك في خطواتك التالية." : "We're honored by your interest! One of our specialists will contact you shortly to answer your questions and guide your next steps."}
          disclaimerTextError={language === 'arabic' ? "تعذر إرسال رسالتك في الوقت الحالي. نرجو منك المحاولة مرة أخرى بعد قليل." : "Your message could not be sent at this moment. We invite you to try again shortly."}
          resetButtonText={language === 'arabic' ? "إعادة تعيين النموذج" : "Reset the form"}
        />

        <LocationSection
          branchInfoSectionPageData={branchInfoSectionPageData}
          language={language}
          subheadingText={language === 'arabic' ? "موقعنا" : "OUR LOCATION"}
          headingText={language === 'arabic' ? "زوروا واحتنا في قلب الرياض" : "Visit our oasis in the heart of Riyadh"}
        />

        <FAQSection
          language={language}
          questions={faqSectionPageData.questions || []}
          subheading={language === 'arabic' ? "الأسئلة الأكثر شيوعاً" : "ESSENTIAL FAQS"}
          heading={language === 'arabic' ? "معلومات أساسية لتجربتك" : "Essential information for your experience"}
          paragraph={language === 'arabic' ? "لقد أجبنا على أسئلتكم الأكثر شيوعًا لتتمكنوا من التخطيط لزيارتكم بسهولة. لم تجدوا ما تحتاجونه؟ فريقنا هنا لمساعدتكم، تواصلوا معنا في أي وقت." : "We've answered your most common questions so you can plan your visit with ease. Can't find what you need? Our team is here to help—reach out anytime."}
        />

      <FooterWrapper
        ctaSectionPageData={ctaSectionPageData}
        brandInfoSectionPageData={brandInfoSectionPageData}
        language={language}
        setLanguage={setLanguage}
      />
    </div>
  )
}
